import type {
  Automation,
  AutomationId,
  AutomationSettings,
  AutomationTemplateId,
} from "@repo/app-types/entities/automation";
import {
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const automationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allAutomations: builder.query<
      Automation[],
      void,
      { data: { automations: Automation[] } }
    >({
      query: () => "/automations",
      transformResponse: (response) => response.data.automations,
      providesTags: (result) =>
        getTagsProvidedByList(TagType.Automation, result),
    }),
    automationById: builder.query<
      Automation,
      AutomationId,
      { data: { automation: Automation } }
    >({
      query: (id) => `/automations/${encodeURIComponent(id)}`,
      transformResponse: (response) => response.data.automation,
      providesTags: (_result, _error, id) =>
        getTagsProvidedByEntity(TagType.Automation, id),
    }),
    createAutomation: builder.mutation<
      Automation,
      {
        template_id: AutomationTemplateId;
        name: string;
        is_enabled?: boolean;
        settings: AutomationSettings;
        session_id?: string;
      },
      { data: { automation: Automation } }
    >({
      query: (body) => ({ method: "POST", url: "/automations", body }),
      transformResponse: (response) => response.data.automation,
      invalidatesTags: () => getTagsInvalidatedByCreate(TagType.Automation),
    }),
    updateAutomation: builder.mutation<
      Automation,
      {
        id: AutomationId;
        name?: string;
        is_enabled?: boolean;
        settings?: AutomationSettings;
        session_id?: string;
      },
      { data: { automation: Automation } }
    >({
      query: ({ id, ...body }) => ({
        method: "PATCH",
        url: `/automations/${encodeURIComponent(id)}`,
        body,
      }),
      transformResponse: (response) => response.data.automation,
      invalidatesTags: (result, error, { id }) =>
        getTagsInvalidatedByUpdate(TagType.Automation, id),
    }),
    deleteAutomation: builder.mutation<void, AutomationId>({
      query: (id) => ({
        method: "DELETE",
        url: `/automations/${encodeURIComponent(id)}`,
      }),
      invalidatesTags: (result, error, id) =>
        getTagsInvalidatedByDelete(TagType.Automation, id),
    }),
  }),
});

export const {
  useAllAutomationsQuery,
  useAutomationByIdQuery,
  useCreateAutomationMutation,
  useUpdateAutomationMutation,
  useDeleteAutomationMutation,
} = automationApi;
